/** Shared applier helpers. Appliers assume validated input (reducer contract). */
import type { ActionType } from "../data/constants";
import type { PlayerId } from "../types/common";
import type { AugmentedWarState, PlayerState } from "../types/game-state";
import { checkTerminal } from "../utils/terminal";

/** Spend 1 AP and bump the per-action counter (rule.md §6). */
export function spendAction(
  state: AugmentedWarState,
  action: ActionType,
): Pick<AugmentedWarState, "apRemaining" | "actionsUsed"> {
  const u = state.actionsUsed;
  return {
    apRemaining: state.apRemaining - 1,
    actionsUsed: {
      move: u.move + (action === "move" ? 1 : 0),
      attack: u.attack + (action === "attack" ? 1 : 0),
      deploy: u.deploy + (action === "deploy" ? 1 : 0),
    },
  };
}

/** Immutably patch one player's state (explicit to avoid computed-key widening). */
export function updatePlayer(
  players: AugmentedWarState["players"],
  id: PlayerId,
  patch: Partial<PlayerState>,
): AugmentedWarState["players"] {
  return id === "p1"
    ? { p1: { ...players.p1, ...patch }, p2: players.p2 }
    : { p1: players.p1, p2: { ...players.p2, ...patch } };
}

/** Apply terminal checks; settle into GAME_END if a win/loss is reached. */
export function finalize(state: AugmentedWarState): AugmentedWarState {
  if (state.phase === "GAME_END") return state;
  const result = checkTerminal(state);
  return result ? { ...state, phase: "GAME_END", result } : state;
}
